'use client'

import React from 'react'
import { useExtracted } from 'next-intl'
import { Minus, Plus } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

/**
 * The zoom control pill for the Edit / Annotate stage — `[−] {zoom%} [+] · Fit`,
 * matching the View lightbox's control so zoom reads the same everywhere. Floats
 * bottom-center over the stage (the bottom bar is already full in the editors).
 * 100% = fit; the Fit reset appears once zoomed.
 */
export function ImageZoomControls({
  zoom,
  canZoomIn,
  canZoomOut,
  zoomByStep,
  reset,
  percentScale = 1,
  floating = true,
  style,
}: {
  zoom: number
  canZoomIn: boolean
  canZoomOut: boolean
  zoomByStep: (factor: number) => void
  reset: () => void
  /**
   * Multiplier applied to the % readout. Surfaces where 100% should mean the
   * image's true 1:1 pixels (the View lightbox) pass their fit scale; editors
   * keep the default (100% = fit).
   */
  percentScale?: number
  /** Float over the stage by default; fullscreen previews render it inline in their footer. */
  floating?: boolean
  /** Position overrides (e.g. lift the pill above a thumbnail strip). */
  style?: React.CSSProperties
}) {
  const t = useExtracted()
  const isZoomed = zoom > 1.001
  return (
    <div
      className={cn(
        'z-20 flex items-center gap-0.5 rounded-md bg-black/50 px-1 py-0.5 text-xs text-white/80 backdrop-blur-md',
        floating && 'absolute bottom-2 left-1/2 -translate-x-1/2',
      )}
      style={style}
    >
      <button
        type="button"
        onClick={() => zoomByStep(1 / 1.5)}
        disabled={!canZoomOut}
        aria-label={t('Zoom out')}
        className="rounded p-1 transition-colors hover:bg-white/10 disabled:opacity-40"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="min-w-[3rem] select-none text-center tabular-nums">
        {isZoomed ? t('{pct}%', { pct: String(Math.round(zoom * percentScale * 100)) }) : t('Fit')}
      </span>
      <button
        type="button"
        onClick={() => zoomByStep(1.5)}
        disabled={!canZoomIn}
        aria-label={t('Zoom in')}
        className="rounded p-1 transition-colors hover:bg-white/10 disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
      {isZoomed && (
        <>
          <span className="mx-0.5 h-4 w-px bg-white/15" aria-hidden="true" />
          <button type="button" onClick={reset} className="rounded px-1.5 py-0.5 transition-colors hover:bg-white/10">
            {t('Fit')}
          </button>
        </>
      )}
    </div>
  )
}
